import React, { useState } from 'react';
import { Lock, User, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { authenticateOfficer } from '../config/loanOfficers';

const LoginModal = ({ onLogin }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Small delay for UX
    setTimeout(() => {
      const officer = authenticateOfficer(code);
      
      if (officer) {
        onLogin(officer);
      } else {
        setError('Invalid code. Please try again.');
        setCode('');
      }
      
      setIsLoading(false);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#002F87] to-[#001a4d] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img 
              src="/vaster_logo.png" 
              alt="Vaster" 
              className="h-10 brightness-0 invert"
            />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Simulation Station
          </h1>
          <p className="text-white/60 text-sm mt-2">
            Loan Calculator for Sales Team
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Yellow accent */}
          <div className="h-1 bg-[#FFFF00]" />
          
          <div className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#002F87]/10 flex items-center justify-center">
                <User className="w-5 h-5 text-[#002F87]" />
              </div>
              <div>
                <h2 className="font-semibold text-[#002F87]">Loan Officer Login</h2>
                <p className="text-sm text-slate-500">Enter your access code</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="code" className="text-sm text-slate-600">
                  Access Code
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="code"
                    data-testid="input-officer-code"
                    type="password"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter your code"
                    className="pl-10 h-12"
                    autoFocus
                    autoComplete="off"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-md">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                data-testid="btn-login"
                className="w-full h-12 bg-[#002F87] hover:bg-[#001a4d] text-white font-semibold"
                disabled={!code.trim() || isLoading}
              >
                {isLoading ? 'Verifying...' : 'Access Calculator'}
              </Button>
            </form>

            <p className="text-xs text-center text-slate-400 mt-6">
              Contact your administrator if you need access credentials
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/40 text-xs mt-6">
          Vaster Capital &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
